/**
 * Início do portal: total de tickets abertos + cards por mesa
 * (GET /portal/summary) e CTA "Abrir novo ticket".
 */
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Plus, Ticket } from 'lucide-react';
import { useAuthUser } from '@/auth/use-auth';
import { useIsImpersonating } from '@/auth/impersonation';
import { Button } from '@/components/ui/button';
import { EmptyState, Skeleton } from '@/components/ui/misc';
import { errorMessage, portalSummaryQuery } from './portal-queries';
import { PortalLink, portalPath } from './portal-nav';

export function PortalDashboard() {
  const user = useAuthUser();
  const summary = useQuery(portalSummaryQuery());
  const firstName = user?.name?.trim().split(/\s+/)[0];
  const impersonating = useIsImpersonating();

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-foreground">
            {firstName ? `Olá, ${firstName}` : 'Olá'}
          </h1>
          <p className="text-sm text-muted-foreground">
            Acompanhe seus atendimentos ou abra uma nova solicitação.
          </p>
        </div>
        {!impersonating && (
          <Button asChild>
            <PortalLink to={portalPath.newTicket}>
              <Plus className="size-4" strokeWidth={2.5} aria-hidden />
              Abrir novo ticket
            </PortalLink>
          </Button>
        )}
      </div>

      {summary.isPending ? (
        <div aria-busy className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="space-y-3 rounded-lg border bg-card p-4 shadow-xs">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-8 w-16" />
            </div>
          ))}
        </div>
      ) : summary.isError ? (
        <div className="rounded-lg border bg-card shadow-xs">
          <EmptyState message={`Erro ao carregar o resumo: ${errorMessage(summary.error)}`} />
        </div>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {/* Total geral */}
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 shadow-xs">
              <div className="flex items-center gap-1.5 text-[13px] font-medium text-muted-foreground">
                <Ticket className="size-4 text-primary" aria-hidden />
                Tickets abertos
              </div>
              <div className="mt-1 text-2xl font-semibold text-primary-pressed tabular-nums">
                {summary.data.totalOpen}
              </div>
            </div>

            {summary.data.openByDesk.map((d) => (
              <div key={d.deskId} className="rounded-lg border bg-card p-4 shadow-xs">
                <div className="truncate text-[13px] font-medium text-muted-foreground">
                  {d.deskName}
                </div>
                <div className="mt-1 text-2xl font-semibold text-foreground tabular-nums">
                  {d.open}
                </div>
                <div className="text-xs text-muted-foreground">
                  {d.open === 1 ? 'ticket aberto' : 'tickets abertos'}
                </div>
              </div>
            ))}
          </div>

          {summary.data.totalOpen === 0 ? (
            <div className="rounded-lg border bg-card shadow-xs">
              <EmptyState message="Você não possui tickets abertos no momento." />
            </div>
          ) : null}
        </>
      )}

      <PortalLink
        to={portalPath.tickets}
        className="inline-flex items-center gap-1 text-sm font-medium text-primary transition-colors hover:text-primary-pressed"
      >
        Ver todos os meus tickets
        <ArrowRight className="size-4" aria-hidden />
      </PortalLink>
    </div>
  );
}
